import { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  ButtonBase,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
  useScrollTrigger,
  useTheme,
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';

import Logo from './Logo';
import navItems from '../data/navigation';
import { COMPANY_EMAIL, phoneHref } from '../data/company';
import { DUR, EASE_CSS, FONT, LAYOUT, RADIUS, TYPE } from '../theme/tokens';

const LANGS = ['ro', 'en'];

// ---------------------------------------------------------------------------
// Materialul.
//
// Trei lucruri fac bara Apple să arate așa cum arată:
//   1. tenta e aproape opacă — sticla stă peste conținut, nu îl lasă să urce
//   2. filtrul e `saturate(180%) blur(24px)`, saturarea ÎNAINTE de blur
//   3. zero ornament: fără gloss, fără lumină pe muchie, fără umbre grele
//
// Tenta și muchia vin din tokeni, deci bara e deschisă pe temă deschisă și
// închisă pe temă închisă, fără nicio ramură în cod.
// ---------------------------------------------------------------------------
const FILTER = 'saturate(180%) blur(24px)';
const NO_BACKDROP =
  '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))';

// Înălțimea și poziția stau în index.css, ca paginile să calculeze `--page-top`
// din aceleași valori. `--nav-h` e înălțimea EXTERIOARĂ, deci conținutul
// primește cei 2px de muchie scăzuți.
const TOOLBAR_H = 'calc(var(--nav-h) - 2px)';

// ---------------------------------------------------------------------------
// Comutatorul de limbă — control segmentat, ca pe iOS.
//
// Varianta veche arăta un singur buton cu limba URMĂTOARE („EN" când site-ul
// era în română), ceea ce se citește exact pe dos de jumătate din oameni: pare
// că spune în ce limbă ești. Aici se văd ambele, iar cea activă e apăsată.
// ---------------------------------------------------------------------------
function LanguageSwitch({ language, onChange, block = false }) {
  const current = language?.startsWith('en') ? 'en' : 'ro';

  return (
    <Box
      role="group"
      aria-label="Limbă / Language"
      sx={{
        display: 'flex',
        flex: 'none',
        p: '2px',
        gap: '2px',
        borderRadius: `${RADIUS.pill}px`,
        backgroundColor: 'var(--qf-nav-hover)',
        width: block ? '100%' : 'auto',
      }}
    >
      {LANGS.map((code) => {
        const active = code === current;

        return (
          <ButtonBase
            key={code}
            onClick={() => onChange(code)}
            aria-pressed={active}
            aria-label={code === 'ro' ? 'Română' : 'English'}
            sx={{
              flex: block ? 1 : 'none',
              // Pe telefon, segmentele lasă loc butonului de schemă și celui
              // de meniu; de la 600px încolo respiră.
              minWidth: block ? 0 : { xs: 33, sm: 38 },
              height: block ? 44 : 30,
              px: 1,
              borderRadius: `${RADIUS.pill}px`,
              fontFamily: FONT.sans,
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '.02em',
              textTransform: 'uppercase',
              color: active ? 'var(--qf-nav-ink)' : 'var(--qf-nav-ink-2)',
              backgroundColor: active ? 'var(--qf-nav-solid)' : 'transparent',
              boxShadow: active ? '0 1px 3px rgba(0,0,0,.12)' : 'none',
              transition: `background-color ${DUR.fast}s ${EASE_CSS}, color ${DUR.fast}s ${EASE_CSS}`,
              '&:focus-visible': { outline: '2px solid var(--qf-accent)', outlineOffset: -2 },
            }}
          >
            {code}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Comutatorul de schemă.
//
// Punctul de plecare rămâne preferința sistemului (`defaultMode="system"` în
// App.js): pe un telefon pus pe întuneric, site-ul e întunecat din prima, fără
// să atingă nimeni butonul. Butonul e pentru cei care vor altceva decât
// sistemul, iar alegerea lor se ține minte (MUI o scrie în localStorage).
//
// Pictograma arată unde ajungi dacă apeși — luna pe fundal deschis, soarele pe
// fundal închis. Starea curentă e evidentă: e chiar pagina din spate.
// ---------------------------------------------------------------------------
function ThemeToggle({ t }) {
  const { mode, systemMode, setMode } = useColorScheme();

  // La primul randare, MUI încă nu știe ce e în localStorage și întoarce
  // `undefined`. Butonul își păstrează locul, dar fără pictogramă, ca să nu
  // apară un cadru cu soarele pe o pagină deschisă.
  const resolved = mode === 'system' ? systemMode : mode;
  const next = resolved === 'dark' ? 'light' : 'dark';
  const label = t(next === 'dark' ? 'nav.themeToDark' : 'nav.themeToLight');

  return (
    <IconButton
      onClick={() => setMode(next)}
      aria-label={label}
      title={label}
      sx={{
        width: 40,
        height: 40,
        flex: 'none',
        color: 'var(--qf-nav-ink-2)',
        '&:hover': { color: 'var(--qf-nav-ink)', backgroundColor: 'var(--qf-nav-hover)' },
        '& svg': { fontSize: 21 },
      }}
    >
      {!resolved ? null : resolved === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
    </IconButton>
  );
}

// ---------------------------------------------------------------------------
export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const reduceMotion = useReducedMotion();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 8 });

  useEffect(() => {
    const saved = localStorage.getItem('language');
    if (saved && saved !== i18n.language) i18n.changeLanguage(saved);
  }, [i18n]);

  useEffect(() => setOpen(false), [location.pathname]);

  // Foaia există doar sub `md`. Fără asta, o rotire a telefonului în peisaj o
  // ascundea vizual, dar lăsa derularea paginii blocată de Modal.
  useEffect(() => {
    if (isDesktop) setOpen(false);
  }, [isDesktop]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const changeLanguage = (code) => {
    if (code === i18n.language) return;
    i18n.changeLanguage(code);
    localStorage.setItem('language', code);
  };

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      <AppBar
        component="nav"
        position="fixed"
        elevation={0}
        // Fără asta, MUI rescrie fundalul barei în schema închisă cu propria
        // culoare (`AppBar.darkBg`), dintr-o regulă pusă înăuntrul unui
        // `@media (prefers-color-scheme: dark)` — adică mai jos în foaia de
        // stil decât a noastră, deci câștigătoare. Sticla dispărea, rămânea un
        // dreptunghi gri opac.
        enableColorOnDark
        sx={{
          // Peste Drawer, ca pilula să rămână deasupra foii care coboară.
          zIndex: theme.zIndex.drawer + 1,

          // Centrare fără `transform`: un transform pe același element cu
          // `backdrop-filter` face Safari să eșantioneze fundalul decalat.
          top: 'var(--nav-top)',
          left: 0,
          right: 0,
          mx: 'auto',
          width: { xs: 'calc(100% - 20px)', sm: 'calc(100% - 40px)' },
          maxWidth: LAYOUT.maxWidth,
          borderRadius: `${RADIUS.pill}px`,
          overflow: 'hidden',
          color: 'var(--qf-nav-ink)',

          backgroundColor: scrolled ? 'var(--qf-nav-bg-scrolled)' : 'var(--qf-nav-bg)',
          backgroundImage: 'none',
          backdropFilter: FILTER,
          WebkitBackdropFilter: FILTER,
          border: '1px solid var(--qf-nav-line)',
          boxShadow: scrolled ? 'var(--qf-nav-shadow)' : 'none',
          transition: `background-color ${DUR.base}s ${EASE_CSS}, box-shadow ${DUR.base}s ${EASE_CSS}`,
          [NO_BACKDROP]: { backgroundColor: 'var(--qf-nav-solid)' },
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            // `theme.mixins.toolbar` impune 56/64px printr-un
            // `@media (min-width:600px)`, care bate o declarație simplă din sx.
            // `@media all` pune regula noastră tot într-un media query, la
            // aceeași specificitate, dar mai jos în foaia de stil.
            '@media all': { minHeight: TOOLBAR_H },
            minHeight: TOOLBAR_H,
            height: TOOLBAR_H,
            pl: { xs: 1.75, sm: 2.25 },
            pr: { xs: 0.75, sm: 1 },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
          }}
        >
          {/* ---- Sigla ---- */}
          <Box
            component={Link}
            to="/"
            aria-label="QuantikFlow — acasă"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 'none',
              minWidth: 0,
              // Zona de atins era doar cât textul; acum e cât un deget.
              minHeight: 44,
              textDecoration: 'none',
              color: 'var(--qf-nav-ink)',
              borderRadius: '10px',
              transition: `opacity ${DUR.fast}s ${EASE_CSS}`,
              '@media (hover: hover)': { '&:hover': { opacity: 0.7 } },
              '&:focus-visible': { outline: '2px solid var(--qf-accent)', outlineOffset: 2 },
            }}
          >
            <Logo
              size={26}
              // Sub 360px (telefoanele foarte mici), numele scris nu mai încape
              // lângă comutatorul de limbă și butonul de meniu. Semnul rămâne.
              nameSx={{ fontSize: 17, '@media (max-width: 359px)': { display: 'none' } }}
            />
          </Box>

          {/* ---- Linkurile de desktop ----
              Fără pilulă și fără fundal la survol: doar textul se aprinde. */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              gap: 0.25,
            }}
          >
            {navItems.map((item) => {
              const active = isActive(item.path);

              return (
                <Box
                  key={item.key}
                  component={Link}
                  to={item.path}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    position: 'relative',
                    // Între 900 și 1200px, cele cinci etichete românești
                    // („Serviciile noastre", „Întrebări frecvente") plus sigla,
                    // comutatorul de limbă și cel de schemă depășesc pilula, iar
                    // `overflow: hidden` taie ultimul buton. De la 1200 încolo e
                    // loc pentru aerul întreg.
                    px: { md: 0.85, lg: 1.75 },
                    py: 1.5,
                    whiteSpace: 'nowrap',
                    textDecoration: 'none',
                    fontSize: '0.9375rem',
                    fontWeight: active ? 600 : 500,
                    letterSpacing: '-0.012em',
                    color: active ? 'var(--qf-nav-ink)' : 'var(--qf-nav-ink-2)',
                    transition: `color ${DUR.fast}s ${EASE_CSS}`,
                    '@media (hover: hover)': { '&:hover': { color: 'var(--qf-nav-ink)' } },
                    '&:focus-visible': {
                      outline: '2px solid var(--qf-accent)',
                      outlineOffset: -2,
                      borderRadius: '8px',
                    },

                    // Singurul accent din bară: un punct sub pagina curentă.
                    '&::after': active
                      ? {
                          content: '""',
                          position: 'absolute',
                          left: '50%',
                          bottom: 6,
                          width: 4,
                          height: 4,
                          marginLeft: '-2px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--qf-accent)',
                        }
                      : {},
                  }}
                >
                  {t(item.key)}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ flex: { xs: 1, md: 'none' } }} />

          {/* ---- Dreapta ---- */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.75 }, flex: 'none' }}>
            <LanguageSwitch language={i18n.language} onChange={changeLanguage} />

            <ThemeToggle t={t} />

            <IconButton
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? 'Închide meniul' : 'Deschide meniul'}
              aria-expanded={open}
              aria-controls="nav-sheet"
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                width: 40,
                height: 40,
                color: 'var(--qf-nav-ink)',
                '&:hover': { backgroundColor: 'var(--qf-nav-hover)' },
              }}
            >
              {open ? <CloseRoundedIcon /> : <MenuRoundedIcon />}
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ---- Foaia de meniu ----
          Apple coboară un panou pe toată lățimea de sub bară, nu un sertar
          lateral: degetul ajunge la orice rând fără să traverseze ecranul. */}
      <Drawer
        id="nav-sheet"
        anchor="top"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        transitionDuration={reduceMotion ? 0 : 340}
        PaperProps={{
          sx: {
            display: { xs: 'block', md: 'none' },
            // Ecranele scunde (telefon în peisaj) nu mai taie ultimul rând.
            maxHeight: '100dvh',
            overflowY: 'auto',
            backgroundColor: 'var(--qf-nav-bg-scrolled)',
            backgroundImage: 'none',
            backdropFilter: FILTER,
            WebkitBackdropFilter: FILTER,
            color: 'var(--qf-nav-ink)',
            boxShadow: 'none',
            borderBottom: '1px solid var(--qf-nav-line)',
            borderBottomLeftRadius: 28,
            borderBottomRightRadius: 28,
            pt: 'calc(var(--nav-top) + var(--nav-h) + 18px)',
            pb: 'max(28px, calc(env(safe-area-inset-bottom) + 16px))',
            paddingInline: LAYOUT.gutter.xs,
            [NO_BACKDROP]: { backgroundColor: 'var(--qf-nav-solid)' },
          },
        }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.4)' } } }}
      >
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {navItems.map((item, i) => {
            const active = isActive(item.path);

            return (
              <Box
                component={motion.li}
                key={item.key}
                initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                animate={open ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
                transition={{ delay: reduceMotion ? 0 : 0.04 + i * 0.04, duration: 0.3 }}
                sx={{ borderBottom: '1px solid var(--qf-nav-line)' }}
              >
                <Box
                  component={Link}
                  to={item.path}
                  onClick={() => setOpen(false)}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    // 58px: loc pentru etichete lungi („Întrebări frecvente")
                    // fără ca rândul să devină o țintă mică.
                    minHeight: 58,
                    py: 1,
                    textDecoration: 'none',
                    fontSize: '1.125rem',
                    fontWeight: active ? 600 : 400,
                    letterSpacing: '-0.018em',
                    color: active ? 'var(--qf-nav-ink)' : 'var(--qf-nav-ink-2)',
                    '&:active': { opacity: 0.6 },
                  }}
                >
                  {t(item.key)}
                  {active && (
                    <Box
                      sx={{
                        flex: 'none',
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        backgroundColor: 'var(--qf-accent)',
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Contactul direct, la un deget distanță din orice pagină. */}
        <Box sx={{ display: 'flex', gap: 1.25, mt: 2.5 }}>
          {[
            { href: `mailto:${COMPANY_EMAIL}`, icon: <MailOutlineRoundedIcon fontSize="small" />, label: t('nav.email') },
            { href: phoneHref, icon: <CallOutlinedIcon fontSize="small" />, label: t('nav.call') },
          ].map((action) => (
            <Box
              key={action.href}
              component="a"
              href={action.href}
              onClick={() => setOpen(false)}
              sx={{
                flex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                minHeight: 50,
                borderRadius: `${RADIUS.pill}px`,
                border: '1px solid var(--qf-nav-line)',
                backgroundColor: 'var(--qf-nav-hover)',
                color: 'var(--qf-nav-ink)',
                textDecoration: 'none',
                ...TYPE.small,
                fontWeight: 600,
                '&:active': { opacity: 0.6 },
              }}
            >
              {action.icon}
              {action.label}
            </Box>
          ))}
        </Box>

        <Typography
          sx={{
            mt: 2.5,
            fontFamily: FONT.mono,
            fontSize: 11.5,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: 'var(--qf-ink-3)',
          }}
        >
          {t('nav.drawerTitle')}
        </Typography>
      </Drawer>
    </>
  );
}
